import { createFileRoute, useNavigate } from '@tanstack/react-router'
import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import {
  dietaryRestrictionsUsersCountQuery,
  ingredientRestrictionsUsersCountQuery,
  topFavoriteIngredientsQuery,
  topFavoriteRecipesQuery,
  topLoggedFoodEntriesQuery,
  topLoggedIngredientsQuery,
  topLoggedOpenFoodFactsProductsQuery,
  topLoggedRecipesQuery,
} from '@/api/usage-statistics'
import { Button } from '@/components/Button/Button'
import { PieChartWrapper } from '@/components/Charts/Charts'
import { Flex } from '@/components/Flex'
import { Grid } from '@/components/Grid'
import { CompactTable } from '@/components/Table/CompactTable'

export type PreferencesSearchParam = {
  topLoggedIngredientsPage?: number
  topLoggedRecipesPage?: number
  topLoggedOpenFoodFactsProductsPage?: number
  topLoggedFoodEntriesPage?: number
  topFavoriteIngredientsPage?: number
  topFavoriteRecipesPage?: number
  topIngredientRestrictionsPage?: number
}

export const Route = createFileRoute('/usage-statistics/preferences')({
  loaderDeps: ({ search }) => search,
  validateSearch: (search: Record<string, unknown>) => {
    return {
      topLoggedIngredientsPage: search.topLoggedIngredientsPage as number,
      topLoggedRecipesPage: search.topLoggedRecipesPage as number,
      topLoggedOpenFoodFactsProductsPage: search.topLoggedOpenFoodFactsProductsPage as number,
      topLoggedFoodEntriesPage: search.topLoggedFoodEntriesPage as number,
      topFavoriteIngredientsPage: search.topFavoriteIngredientsPage as number,
      topFavoriteRecipesPage: search.topFavoriteRecipesPage as number,
      ingredientRestrictionsUsersCountPage: search.ingredientRestrictionsUsersCountPage as number,
    } as PreferencesSearchParam
  },
  loader: async ({ context: { queryClient }, deps }) => {
    const [
      topLoggedIngredients,
      topLoggedRecipes,
      topLoggedOpenFoodFactsProducts,
      topLoggedFoodEntries,
      topFavoriteIngredients,
      topFavoriteRecipes,
      dietaryRestrictionsUsersCount,
      ingredientRestrictionsUsersCount,
    ] = await Promise.all([
      queryClient.ensureQueryData(
        topLoggedIngredientsQuery({ page: deps.topLoggedIngredientsPage }),
      ),
      queryClient.ensureQueryData(topLoggedRecipesQuery({ page: deps.topLoggedRecipesPage })),
      queryClient.ensureQueryData(
        topLoggedOpenFoodFactsProductsQuery({ page: deps.topLoggedOpenFoodFactsProductsPage }),
      ),
      queryClient.ensureQueryData(
        topLoggedFoodEntriesQuery({ page: deps.topLoggedFoodEntriesPage }),
      ),
      queryClient.ensureQueryData(
        topFavoriteIngredientsQuery({ page: deps.topFavoriteIngredientsPage }),
      ),
      queryClient.ensureQueryData(topFavoriteRecipesQuery({ page: deps.topFavoriteRecipesPage })),
      queryClient.ensureQueryData(dietaryRestrictionsUsersCountQuery()),
      queryClient.ensureQueryData(
        ingredientRestrictionsUsersCountQuery({ page: deps.topIngredientRestrictionsPage }),
      ),
    ])

    return {
      topLoggedIngredients,
      topLoggedRecipes,
      topLoggedOpenFoodFactsProducts,
      topLoggedFoodEntries,
      topFavoriteIngredients,
      topFavoriteRecipes,
      dietaryRestrictionsUsersCount,
      ingredientRestrictionsUsersCount,
    }
  },
  component: RouteComponent,
})

const DEFAULT_PAGE_SIZE = 20 // Mirror backend API pagination size to drive Next button availability

function Pager({
  page,
  canPrev,
  canNext,
  onPrev,
  onNext,
}: {
  page: number
  canPrev: boolean
  canNext: boolean
  onPrev(): void
  onNext(): void
}) {
  const { t } = useTranslation()
  return (
    <Flex justify="space-between" align="center" style={{ marginTop: 8 }}>
      <span>{t('rowCount', { count: (page + 1) * DEFAULT_PAGE_SIZE })}</span>
      <Flex gap={8}>
        <Button onPress={onPrev} isDisabled={!canPrev}>
          {String(t('usageStats.prev' as any))}
        </Button>
        <Button onPress={onNext} isDisabled={!canNext}>
          {String(t('usageStats.next' as any))}
        </Button>
      </Flex>
    </Flex>
  )
}

function RouteComponent() {
  const {
    topLoggedIngredients,
    topLoggedRecipes,
    topLoggedOpenFoodFactsProducts,
    topLoggedFoodEntries,
    topFavoriteIngredients,
    topFavoriteRecipes,
    dietaryRestrictionsUsersCount,
    ingredientRestrictionsUsersCount: topIngredientRestrictions,
  } = Route.useLoaderData()
  const search = Route.useSearch() as PreferencesSearchParam
  const navigate = useNavigate({ from: '/usage-statistics/preferences' })
  const { t } = useTranslation()
  const nf = useMemo(() => new Intl.NumberFormat(undefined), [])

  // Common columns for name/count tables
  const NAME_COL = { key: 'name', label: t('ingredient.name') }
  const COUNT_COL = {
    key: 'count',
    label: t('ingredient.count'),
    allowSorting: false,
    width: 50,
    render: (row: any) => nf.format(row.count),
  }
  const USER_COUNT_COL = {
    key: 'userCount',
    label: t('usageStats.userCount'),
    width: 70,
    allowSorting: false,
    render: (row: any) => (row.userCount !== undefined ? nf.format(row.userCount) : '-'),
  }

  const goToPage = (key: keyof PreferencesSearchParam, nextPage: number) => {
    navigate({
      search: (prev) => ({ ...prev, [key]: Math.max(0, nextPage) }),
      replace: false,
    })
  }

  return (
    <>
      <div className="panel-xs" style={{ marginTop: 24 }}>
        <h2 style={{ marginTop: 0 }}>{t('usageStats.dietaryRestrictionsUsersCount')}</h2>
        <Grid columns="repeat(auto-fit, minmax(320px, 1fr))" gap={16}>
          <div>
            <PieChartWrapper
              title={undefined}
              data={(dietaryRestrictionsUsersCount ?? []).map((s) => ({
                name: t(`recipe.dietOptions.${s.name}` as any),
                value: s.count,
              }))}
            />
          </div>
        </Grid>
      </div>

      {/* Top activity tables */}
      <Grid columns="1fr 1fr 1fr" gap={24} style={{ marginTop: 24 }}>
        <section className="panel-xs">
          <h2>{t('usageStats.topLoggedIngredients')}</h2>
          <CompactTable
            aria-label="Top logged ingredients"
            key="top-logged-ingredients"
            columns={[NAME_COL, USER_COUNT_COL, COUNT_COL]}
            rows={topLoggedIngredients}
            rowId="id"
            search
            pagination={false}
          />
          <Pager
            page={search.topLoggedIngredientsPage ?? 0}
            canPrev={(search.topLoggedIngredientsPage ?? 0) > 0}
            canNext={(topLoggedIngredients?.length ?? 0) >= DEFAULT_PAGE_SIZE}
            onPrev={() =>
              goToPage('topLoggedIngredientsPage', (search.topLoggedIngredientsPage ?? 0) - 1)
            }
            onNext={() =>
              goToPage('topLoggedIngredientsPage', (search.topLoggedIngredientsPage ?? 0) + 1)
            }
          />
        </section>

        <section className="panel-xs">
          <h2>{t('usageStats.topLoggedRecipes')}</h2>
          <CompactTable
            aria-label="Top logged recipes"
            columns={[NAME_COL, USER_COUNT_COL, COUNT_COL]}
            rows={topLoggedRecipes}
            rowId="id"
            search
            pagination={false}
            onLinkAction={(row: any) => `/recipes/${row.id}`}
          />
          <Pager
            page={search.topLoggedRecipesPage ?? 0}
            canPrev={(search.topLoggedRecipesPage ?? 0) > 0}
            canNext={(topLoggedRecipes?.length ?? 0) >= DEFAULT_PAGE_SIZE}
            onPrev={() => goToPage('topLoggedRecipesPage', (search.topLoggedRecipesPage ?? 0) - 1)}
            onNext={() => goToPage('topLoggedRecipesPage', (search.topLoggedRecipesPage ?? 0) + 1)}
          />
        </section>

        <section className="panel-xs">
          <h2>{t('usageStats.topLoggedOFFProducts')}</h2>
          <CompactTable
            aria-label="Top logged OFF products"
            columns={[NAME_COL, USER_COUNT_COL, COUNT_COL]}
            rows={topLoggedOpenFoodFactsProducts}
            rowId="id"
            search
            pagination={false}
          />
          <Pager
            page={search.topLoggedOpenFoodFactsProductsPage ?? 0}
            canPrev={(search.topLoggedOpenFoodFactsProductsPage ?? 0) > 0}
            canNext={(topLoggedOpenFoodFactsProducts?.length ?? 0) >= DEFAULT_PAGE_SIZE}
            onPrev={() =>
              goToPage(
                'topLoggedOpenFoodFactsProductsPage',
                (search.topLoggedOpenFoodFactsProductsPage ?? 0) - 1,
              )
            }
            onNext={() =>
              goToPage(
                'topLoggedOpenFoodFactsProductsPage',
                (search.topLoggedOpenFoodFactsProductsPage ?? 0) + 1,
              )
            }
          />
        </section>

        <section className="panel-xs">
          <h2>{t('usageStats.topLoggedFoodEntries')}</h2>
          <CompactTable
            aria-label="Top logged food entries"
            columns={[NAME_COL, USER_COUNT_COL, COUNT_COL]}
            rows={topLoggedFoodEntries}
            search
            pagination={false}
          />
          <Pager
            page={search.topLoggedFoodEntriesPage ?? 0}
            canPrev={(search.topLoggedFoodEntriesPage ?? 0) > 0}
            canNext={(topLoggedFoodEntries?.length ?? 0) >= DEFAULT_PAGE_SIZE}
            onPrev={() =>
              goToPage('topLoggedFoodEntriesPage', (search.topLoggedFoodEntriesPage ?? 0) - 1)
            }
            onNext={() =>
              goToPage('topLoggedFoodEntriesPage', (search.topLoggedFoodEntriesPage ?? 0) + 1)
            }
          />
        </section>

        <section className="panel-xs">
          <h2>{t('usageStats.topFavoriteIngredients')}</h2>
          <CompactTable
            aria-label="Top favorite ingredients"
            columns={[NAME_COL, COUNT_COL]}
            rows={topFavoriteIngredients}
            rowId="id"
            search
            pagination={false}
          />
          <Pager
            page={search.topFavoriteIngredientsPage ?? 0}
            canPrev={(search.topFavoriteIngredientsPage ?? 0) > 0}
            canNext={(topFavoriteIngredients?.length ?? 0) >= DEFAULT_PAGE_SIZE}
            onPrev={() =>
              goToPage('topFavoriteIngredientsPage', (search.topFavoriteIngredientsPage ?? 0) - 1)
            }
            onNext={() =>
              goToPage('topFavoriteIngredientsPage', (search.topFavoriteIngredientsPage ?? 0) + 1)
            }
          />
        </section>

        <section className="panel-xs">
          <h2>{t('usageStats.topFavoriteRecipes')}</h2>
          <CompactTable
            aria-label="Top favorite recipes"
            columns={[NAME_COL, COUNT_COL]}
            rows={topFavoriteRecipes}
            rowId="id"
            search
            pagination={false}
            onLinkAction={(row: any) => `/recipes/${row.id}`}
          />
          <Pager
            page={search.topFavoriteRecipesPage ?? 0}
            canPrev={(search.topFavoriteRecipesPage ?? 0) > 0}
            canNext={(topFavoriteRecipes?.length ?? 0) >= DEFAULT_PAGE_SIZE}
            onPrev={() =>
              goToPage('topFavoriteRecipesPage', (search.topFavoriteRecipesPage ?? 0) - 1)
            }
            onNext={() =>
              goToPage('topFavoriteRecipesPage', (search.topFavoriteRecipesPage ?? 0) + 1)
            }
          />
        </section>

        <section className="panel-xs">
          <h2>{t('usageStats.topIngredientRestrictions')}</h2>
          <CompactTable
            aria-label="Top ingredient restrictions"
            columns={[NAME_COL, COUNT_COL]}
            rows={topIngredientRestrictions}
            rowId="id"
            search
            pagination={false}
          />
          <Pager
            page={search.topIngredientRestrictionsPage ?? 0}
            canPrev={(search.topIngredientRestrictionsPage ?? 0) > 0}
            canNext={(topIngredientRestrictions?.length ?? 0) >= DEFAULT_PAGE_SIZE}
            onPrev={() =>
              goToPage(
                'topIngredientRestrictionsPage',
                (search.topIngredientRestrictionsPage ?? 0) - 1,
              )
            }
            onNext={() =>
              goToPage(
                'topIngredientRestrictionsPage',
                (search.topIngredientRestrictionsPage ?? 0) + 1,
              )
            }
          />
        </section>
      </Grid>
    </>
  )
}
